import { useEffect, useRef, useState } from "react";
import { Send, Lock } from "lucide-react";
import { api, API, errMsg, fmtDateTime } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Section } from "@/components/common";

export default function Chat({ bookingId, readOnly }) {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const ws = useRef(null);
  const end = useRef(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
    const sock = new WebSocket(`${API.replace(/^http/, "ws")}/ws/bookings/${bookingId}`);
    sock.onmessage = (e) => {
      const m = JSON.parse(e.data);
      if (m.error) setError(m.error);
      else setMsgs((xs) => (xs.some((x) => x.id === m.id) ? xs : [...xs, m]));
    };
    ws.current = sock;
    return () => { sock.close(); };
  }, [bookingId]);

  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setError("");
    if (ws.current?.readyState === 1) ws.current.send(JSON.stringify({ text }));
    else {
      try { const r = await api.post(`/bookings/${bookingId}/messages`, { text }); setMsgs((xs) => [...xs, r.data]); } catch (err) { setError(errMsg(err)); }
    }
    setText("");
  };

  return (
    <Section title={t("bk.chat")} testId="booking-chat">
      <div className="h-64 overflow-y-auto space-y-2 pr-1" aria-live="polite" data-testid="chat-messages">
        {!msgs.length && <p className="w-muted">Noch keine Nachrichten.</p>}
        {msgs.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`max-w-[80%] rounded-2xl px-4 py-2 ${mine ? "ml-auto bg-mint-soft" : "bg-gray-100"}`}>
              <p className="text-sm font-semibold w-muted">{mine ? "Du" : m.sender_name} · {fmtDateTime(m.created_at, lang)}</p>
              <p>{m.text}</p>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      {error && <p role="alert" className="text-[var(--w-error)] font-semibold mt-2">{error}</p>}
      {readOnly ? (
        <p className="w-muted mt-3 flex items-center gap-2" data-testid="chat-readonly"><Lock size={16} aria-hidden="true" /> {t("bk.chat_ro")}</p>
      ) : (
        <form onSubmit={send} className="flex gap-2 mt-3">
          <input className="w-input" aria-label={t("bk.msg")} placeholder={t("bk.msg")} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} data-testid="chat-input" />
          <button className="w-btn w-btn-primary !px-4" aria-label={t("bk.send")} data-testid="chat-send-btn"><Send size={18} aria-hidden="true" /></button>
        </form>
      )}
    </Section>
  );
}
